import { useQuery } from '@tanstack/react-query'
import { getAttachableAgents, type AttachableAgent } from '../transport/api'
import type { GameAgentPick } from '../chatv1/transport'
import { Icon } from '../ui/Icon'

/**
 * One of YOUR agents, picked to play against you or to give you hints — the same list a project's
 * "Attach agents" draws from (each with the machine it runs on, which the game needs to reach it).
 */
export function AgentPick({
  picked,
  onPick,
}: {
  picked: GameAgentPick | null
  onPick: (a: GameAgentPick) => void
}) {
  const agents = useQuery({ queryKey: ['attachable-agents'], queryFn: () => getAttachableAgents() })
  const rows = (agents.data?.agents ?? []).filter((a): a is AttachableAgent & { device_id: string } => !!a.device_id)
  if (agents.isPending) return <p className="gh-note">Loading your agents…</p>
  if (agents.isError) return <p className="gh-note mch-err">Your agents could not be loaded — try again.</p>
  if (!rows.length) {
    return <p className="gh-note">No agents yet. Start one with ＋ → Agent, then come back.</p>
  }
  return (
    <div className="ac-menu gm-agents" role="radiogroup" aria-label="Your agents">
      {rows.map((a) => {
        const on = picked?.agent_hash === a.channel_hash && picked.device_id === a.device_id
        return (
          <button
            key={`${a.device_id}/${a.channel_hash}`}
            type="button"
            role="radio"
            aria-checked={on}
            className={`ac-row${on ? ' on' : ''}`}
            onClick={() => onPick({ agent_hash: a.channel_hash, device_id: a.device_id, name: a.label })}
          >
            <Icon name="smart_toy" size={18} />
            <span className="ac-row-label">{a.label}</span>
            {a.device_name && <span className="ac-row-val">{a.device_name}</span>}
            {on && <Icon name="check" size={18} className="ac-row-go" />}
          </button>
        )
      })}
    </div>
  )
}
